import moment from "moment";

import { RecurringTransactionsResponse } from "../../../../services/recurringTransactions/types";
import { getNumberFormat, TransactionType } from "../../../../utils";
import { Frequency } from "../../panels/transactions/types";
import { AmountVariant, Countdown, DayBadge, RecurringSummary } from "./types";

// Days inside which a recurring charge is considered "due soon" (amber pill).
const SOON_THRESHOLD_DAYS = 3;
// Look-ahead window for the "next 14d" summary figure.
const NEXT_WINDOW_DAYS = 14;

// Approximate number of occurrences per month for each base frequency.
const FREQUENCY_TO_MONTHS: Record<Frequency, number> = {
  [Frequency.DAILY]: 30,
  [Frequency.WEEKLY]: 52 / 12,
  [Frequency.MONTHLY]: 1,
  [Frequency.ANNUALLY]: 1 / 12,
};

const FREQUENCY_LABELS: Record<Frequency, string> = {
  [Frequency.DAILY]: "DAILY",
  [Frequency.WEEKLY]: "WEEKLY",
  [Frequency.MONTHLY]: "MONTHLY",
  [Frequency.ANNUALLY]: "YEARLY",
};

const FREQUENCY_UNITS: Record<Frequency, string> = {
  [Frequency.DAILY]: "DAYS",
  [Frequency.WEEKLY]: "WEEKS",
  [Frequency.MONTHLY]: "MONTHS",
  [Frequency.ANNUALLY]: "YEARS",
};

export const getAmountVariant = (type: TransactionType): AmountVariant => {
  if (type === TransactionType.INCOME) {
    return "income";
  }
  if (type === TransactionType.TRANSFER) {
    return "neutral";
  }
  return "expense";
};

export const getAmountSign = (variant: AmountVariant): string => {
  if (variant === "income") {
    return "+";
  }
  if (variant === "expense") {
    return "-";
  }
  return "";
};

export const formatSignedAmount = (
  amount: number,
  variant: AmountVariant,
): string => {
  return `${getAmountSign(variant)}${getNumberFormat(Math.abs(amount))} €`;
};

const toVariant = (value: number): AmountVariant => {
  if (value > 0) {
    return "income";
  }
  if (value < 0) {
    return "expense";
  }
  return "neutral";
};

const toMonthlyEquivalent = (
  amount: number,
  frequency: Frequency,
  interval: number,
): number => {
  const multiplier = FREQUENCY_TO_MONTHS[frequency] ?? 1;
  const safeInterval = interval > 0 ? interval : 1;
  return (amount * multiplier) / safeInterval;
};

// Signed contribution of a single rule (income positive, expense negative,
// transfers ignored).
const getSignedAmount = (
  transaction: RecurringTransactionsResponse,
  amount: number,
): number => {
  if (transaction.transactionType === TransactionType.TRANSFER) {
    return 0;
  }
  if (transaction.transactionType === TransactionType.INCOME) {
    return amount;
  }
  return -amount;
};

export const computeRecurringSummary = (
  recurringTransactions: RecurringTransactionsResponse[],
): RecurringSummary => {
  const today = moment().startOf("day");
  const windowEnd = today.clone().add(NEXT_WINDOW_DAYS, "days");

  let monthlyNet = 0;
  let next14dSum = 0;

  for (const transaction of recurringTransactions) {
    const monthly = toMonthlyEquivalent(
      transaction.amount,
      transaction.frequency,
      transaction.interval,
    );
    monthlyNet += getSignedAmount(transaction, monthly);

    const nextDate = moment(transaction.nextExecutionDate).startOf("day");
    const isWithinWindow =
      nextDate.isSameOrAfter(today) && nextDate.isSameOrBefore(windowEnd);
    if (isWithinWindow) {
      next14dSum += getSignedAmount(transaction, transaction.amount);
    }
  }

  return {
    activeCount: recurringTransactions.length,
    monthlyNet,
    monthlyVariant: toVariant(monthlyNet),
    next14dSum,
    next14dVariant: toVariant(next14dSum),
  };
};

export const getFrequencyLabel = (
  frequency: Frequency,
  interval: number,
): string => {
  const safeInterval = interval > 0 ? interval : 1;
  if (frequency === Frequency.WEEKLY && safeInterval === 2) {
    return "BIWEEKLY";
  }
  if (safeInterval === 1) {
    return FREQUENCY_LABELS[frequency];
  }
  return `EVERY ${safeInterval} ${FREQUENCY_UNITS[frequency]}`;
};

export const getCardTitle = (
  transaction: RecurringTransactionsResponse,
): string => {
  return transaction.note || transaction.categoryName || " - ";
};

export const getDayBadge = (dateString: string): DayBadge => {
  const date = moment(dateString);
  return {
    day: date.format("D"),
    month: date.format("MMM").toUpperCase(),
  };
};

export const getCountdown = (dateString: string): Countdown => {
  const today = moment().startOf("day");
  const target = moment(dateString).startOf("day");
  const days = target.diff(today, "days");

  if (days < 0) {
    return { label: "OVERDUE", isSoon: true };
  }
  if (days === 0) {
    return { label: "TODAY", isSoon: true };
  }
  return { label: `IN ${days} D`, isSoon: days <= SOON_THRESHOLD_DAYS };
};
